<script lang="ts">
import { cva } from 'class-variance-authority'

export type SheetSide = 'top' | 'bottom' | 'left' | 'right'

export const sheetVariants = cva(
  'fixed z-50 bg-background shadow-lg flex flex-col focus:outline-none',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b data-[state=open]:animate-sheet-slide-in-from-top data-[state=closed]:animate-sheet-slide-out-to-top',
        bottom: 'inset-x-0 bottom-0 border-t data-[state=open]:animate-sheet-slide-in-from-bottom data-[state=closed]:animate-sheet-slide-out-to-bottom',
        left: 'inset-y-0 left-0 h-full w-3/4 border-r data-[state=open]:animate-sheet-slide-in-from-left data-[state=closed]:animate-sheet-slide-out-to-left',
        right: 'inset-y-0 right-0 h-full w-3/4 border-l data-[state=open]:animate-sheet-slide-in-from-right data-[state=closed]:animate-sheet-slide-out-to-right',
      },
    },
    defaultVariants: { side: 'right' },
  },
)
</script>

<script setup lang="ts">
import type { DialogContentEmits, DialogContentProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { computed } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { DialogClose, DialogContent, DialogOverlay, DialogPortal, useForwardPropsEmits } from 'reka-ui'
import { X } from 'lucide-vue-next'
import { cn } from '@/lib/utils'

interface Props extends DialogContentProps {
  class?: HTMLAttributes['class']
  side?: SheetSide
  showClose?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  side: 'right',
  showClose: true,
})

const emits = defineEmits<DialogContentEmits>()

const delegatedProps = reactiveOmit(props, 'class', 'side', 'showClose')
const forwarded = useForwardPropsEmits(delegatedProps, emits)

const contentClass = computed(() => cn(sheetVariants({ side: props.side }), props.class))
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      class="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-sheet-fade-in data-[state=closed]:animate-sheet-fade-out"
    />
    <DialogContent v-bind="forwarded" :class="contentClass">
      <slot />
      <DialogClose
        v-if="showClose"
        class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
      >
        <X class="size-4" />
        <span class="sr-only">닫기</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
